import { HomeContainerContentGroupCard } from './HomeContainerContentGroupCard';
import { Logger } from '@/classes';
import { ContentCard } from '@/entities/content';
import { useHorizontalScroll } from '@/features/horizontal-scroll';
import { Expose } from '@/shared/ui';
import { HomeContainer } from '@/types';
import { FOCUS_ROW_ATTRIBUTE, isFocusScrollSuppressed, scrollElementTo } from '@/utils';
import { useCallback, useMemo, useRef } from 'react';

export type HomeContainerCardProps = {
  sourcePage: string;
  index: number;
  homeContainer: HomeContainer;
  onFocused?: ({ x, y, left, top }: { x: number; y: number; left: number; top: number }) => void;
};

export const HomeContainerCard = ({ sourcePage, index, homeContainer, onFocused }: HomeContainerCardProps) => {
  // ! 줄 단위 포커스 컨테이너를 두지 않는다.
  // ! 컨테이너가 있으면 줄마다 마지막 포커스를 기억해서, 아래 키를 눌렀을 때 바로 밑의 카드가 아니라
  // ! 그 줄에서 마지막에 봤던 카드로 이동한다. 모든 카드를 형제로 두면 norigin 의 좌표 기반 탐색이
  // ! 바로 밑의 카드를 골라준다. (가로 스크롤은 focus 컨테이너와 무관하게 아래 ref 로 처리한다)
  const ref = useRef<HTMLDivElement>(null);

  useHorizontalScroll({ scrollingRef: ref });

  const handleFocused = useCallback(
    ({ x, y, left, top }: { x: number; y: number; left: number; top: number }) => {
      // 가로 스크롤만 이 컴포넌트 책임. 세로 스크롤은 아래 onFocused(부모)가 자체적으로 판단한다.
      if (!isFocusScrollSuppressed('horizontal')) {
        const current = ref.current;

        if (current) {
          scrollElementTo({ element: current, left: left - 400 });
        }
      }

      if (onFocused) {
        onFocused({ x, y, left, top });
      }
    },
    [onFocused, ref],
  );

  const component = useMemo(() => {
    switch (homeContainer.viewType) {
      case 'RECOMMEND_CONTENT_LIST':
      case 'CONTENT_LIST': {
        return homeContainer.contents
          .filter((content) => content.type === 'VIDEO')
          .map((content, i) => {
            return (
              <ContentCard
                sourcePage={sourcePage}
                key={content.id}
                tabIndex={i}
                variant="default"
                content={content}
                focusKey={`content-${index}-${i}`}
                onFocused={handleFocused}
                containerId={homeContainer.id}
                weight={i}
              />
            );
          });
      }
      case 'RANKING_CONTENT_LIST_A':
      case 'RANKING_CONTENT_LIST_B': {
        return homeContainer.contents
          .filter((content) => content.type === 'VIDEO')
          .map((content, i) => {
            return (
              <ContentCard
                sourcePage={sourcePage}
                key={content.id}
                tabIndex={i}
                variant="rank"
                rank={i + 1}
                content={content}
                focusKey={`content-${index}-${i}`}
                onFocused={handleFocused}
                containerId={homeContainer.id}
                weight={i}
              />
            );
          });
      }
      case 'CONTENT_GROUP': {
        return (
          <>
            {homeContainer.contentGroup && (
              <HomeContainerContentGroupCard
                key={homeContainer.contentGroup.id}
                focusKey={`content-group-${index}`}
                contentGroup={homeContainer.contentGroup}
                onFocus={handleFocused}
                containerId={homeContainer.id}
                weight={index}
              />
            )}
            {homeContainer.contents
              .filter((content) => content.type === 'VIDEO')
              .map((content, i) => {
                return (
                  <ContentCard
                    sourcePage={sourcePage}
                    key={content.id}
                    tabIndex={i}
                    variant="default"
                    content={content}
                    focusKey={`content-${index}-${i}`}
                    onFocused={handleFocused}
                    containerId={homeContainer.id}
                    weight={i}
                  />
                );
              })}
          </>
        );
      }
    }
  }, [
    handleFocused,
    homeContainer.contentGroup,
    homeContainer.contents,
    homeContainer.id,
    homeContainer.viewType,
    index,
    sourcePage,
  ]);

  const alreadyExposedRef = useRef(false);
  const handleExposed = useCallback(() => {
    if (!alreadyExposedRef.current) {
      Logger.exposedHomeContainer(homeContainer.id, index);
      alreadyExposedRef.current = true;
    }
  }, [homeContainer.id, index]);

  return (
    <Expose onExposed={handleExposed}>
      <div className="mb-[16px]">
        {homeContainer.header && (
          <div className="px-[60px] flex items-center">
            {homeContainer.header.icon && (
              <div className="w-[40px] h-[48px] flex items-center justify-center">
                <img src={homeContainer.header.icon} className="w-[32px] h-[32px]" />
              </div>
            )}

            <h3 className="font-bold text-[32px] text-[#000000] ml-[8px]">{homeContainer.header.title}</h3>
          </div>
        )}

        <div className="overflow-x-scroll" ref={ref} {...{ [FOCUS_ROW_ATTRIBUTE]: '' }}>
          <div className="flex items-center py-4 w-fit px-[60px]">
            {component}
            {/* {loading && skeletonComponents} */}
          </div>
        </div>
      </div>
    </Expose>
  );
};
